import { Cancel, CheckCircle } from "@mui/icons-material";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import {
	DocumentStatus,
	DocumentType,
	useAcceptOfferMutation,
	useGetAllCommercialDocumentsLazyQuery,
	useRejectOfferMutation,
} from "@repo/server-api";
import { useNotification } from "@repo/shared/components";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import React, { forwardRef, useImperativeHandle, useState } from "react";

export type DialogProjectOffersHandle = {
	show: (projectId: string, projectName: string) => void;
};

export const DialogProjectOffers = forwardRef<DialogProjectOffersHandle>((_props, ref) => {
	const { t, i18n } = useTranslation();
	const { showNotification } = useNotification();

	const [open, setOpen] = useState(false);
	const [projectId, setProjectId] = useState<string | null>(null);
	const [projectName, setProjectName] = useState<string>("");
	const [rejectReasonMap, setRejectReasonMap] = useState<Record<string, string>>({});
	const [rejectingId, setRejectingId] = useState<string | null>(null);

	const [getOffers, { data, loading }] = useGetAllCommercialDocumentsLazyQuery({
		fetchPolicy: "no-cache",
	});

	const [acceptOffer, { loading: acceptLoading }] = useAcceptOfferMutation({
		fetchPolicy: "no-cache",
		onCompleted(result) {
			if (result.acceptOffer?.errors?.length) {
				result.acceptOffer.errors.forEach((e) => showNotification(e.message, "error"));
				return;
			}
			showNotification(t("offer-accepted-successfully"), "success");
			reloadOffers();
		},
		onError(error) {
			showNotification(error.message, "error");
		},
	});

	const [rejectOffer, { loading: rejectLoading }] = useRejectOfferMutation({
		fetchPolicy: "no-cache",
		onCompleted(result) {
			if (result.rejectOffer?.errors?.length) {
				result.rejectOffer.errors.forEach((e) => showNotification(e.message, "error"));
				return;
			}
			showNotification(t("offer-rejected-successfully"), "success");
			setRejectingId(null);
			reloadOffers();
		},
		onError(error) {
			showNotification(error.message, "error");
		},
	});

	const reloadOffers = () => {
		if (!projectId) return;
		getOffers({
			variables: {
				first: 100,
				after: "0",
				startDate: "2000-01-01",
				ofType: [DocumentType.Offer],
				projectId,
			},
		});
	};

	useImperativeHandle(ref, () => ({
		show: (pid: string, pname: string) => {
			setProjectId(pid);
			setProjectName(pname);
			setRejectReasonMap({});
			setRejectingId(null);
			setOpen(true);
			getOffers({
				variables: {
					first: 100,
					after: "0",
					startDate: "2000-01-01",
					ofType: [DocumentType.Offer],
					projectId: pid,
				},
			});
		},
	}));

	const offers = data?.allCommercialDocuments?.nodes ?? [];
	const dateFormat = i18n.language === "de" ? "DD.MM.YYYY" : "MM/DD/YYYY";

	const getStatusColor = (
		status: DocumentStatus,
	): "success" | "error" | "primary" | "default" | "warning" => {
		switch (status) {
			case DocumentStatus.Accepted:
				return "success";
			case DocumentStatus.Rejected:
				return "error";
			case DocumentStatus.Finalized:
			case DocumentStatus.Sent:
				return "primary";
			case DocumentStatus.Draft:
				return "warning";
			default:
				return "default";
		}
	};

	const canAccept = (status: DocumentStatus) =>
		status === DocumentStatus.Finalized ||
		status === DocumentStatus.Sent ||
		status === DocumentStatus.Rejected;
	const canReject = (status: DocumentStatus) =>
		status === DocumentStatus.Finalized || status === DocumentStatus.Sent;

	const handleAccept = (id: string) => {
		acceptOffer({ variables: { id } });
	};

	const handleStartReject = (id: string) => {
		setRejectingId(id);
		setRejectReasonMap((prev) => ({ ...prev, [id]: prev[id] ?? "" }));
	};

	const handleConfirmReject = (id: string) => {
		rejectOffer({ variables: { id, reason: rejectReasonMap[id] ?? "" } });
	};

	const formatCurrency = (amount: number) =>
		new Intl.NumberFormat(i18n.language, { style: "currency", currency: "EUR" }).format(amount);

	return (
		<Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
			<DialogTitle>
				{t("project-offers")} — {projectName}
			</DialogTitle>
			<DialogContent>
				{loading ? (
					<Box display="flex" justifyContent="center" py={4}>
						<CircularProgress />
					</Box>
				) : offers.length === 0 ? (
					<Typography color="text.secondary" py={2}>
						{t("no-offers-found")}
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>{t("offer-number")}</TableCell>
								<TableCell>{t("issue-date")}</TableCell>
								<TableCell>{t("due-date")}</TableCell>
								<TableCell align="right">{t("gross-total")}</TableCell>
								<TableCell>{t("status")}</TableCell>
								<TableCell>{t("actions")}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{offers.map((offer) => (
								<React.Fragment key={offer.id}>
									<TableRow>
										<TableCell>{offer.number}</TableCell>
										<TableCell>
											{offer.issueDate ? dayjs(offer.issueDate).format(dateFormat) : "—"}
										</TableCell>
										<TableCell>
											{offer.dueDate ? dayjs(offer.dueDate).format(dateFormat) : "—"}
										</TableCell>
										<TableCell align="right">{formatCurrency(offer.grossTotal)}</TableCell>
										<TableCell>
											<Chip
												label={t(`document-status.${offer.status.toLowerCase()}`)}
												color={getStatusColor(offer.status as DocumentStatus)}
												size="small"
											/>
										</TableCell>
										<TableCell>
											<Box display="flex" gap={1}>
												{canAccept(offer.status as DocumentStatus) && (
													<IconButton
														size="small"
														color="success"
														onClick={() => handleAccept(offer.id ?? "")}
														data-testid="accept-offer-button"
														disabled={acceptLoading}>
														<CheckCircle />
													</IconButton>
												)}
												{canReject(offer.status as DocumentStatus) && (
													<IconButton
														size="small"
														color="error"
														onClick={() => handleStartReject(offer.id ?? "")}
														data-testid="reject-offer-button"
														disabled={rejectLoading && rejectingId === offer.id}>
														<Cancel />
													</IconButton>
												)}
											</Box>
										</TableCell>
									</TableRow>
									{rejectingId === offer.id && (
										<TableRow>
											<TableCell colSpan={6}>
												<Box display="flex" gap={1} alignItems="center" py={1}>
													<TextField
														size="small"
														label={t("rejection-reason")}
														value={rejectReasonMap[offer.id ?? ""] ?? ""}
														onChange={(e) =>
															setRejectReasonMap((prev) => ({
																...prev,
																[offer.id ?? ""]: e.target.value,
															}))
														}
														fullWidth
													/>
													<Button
														variant="contained"
														color="error"
														size="small"
														onClick={() => handleConfirmReject(offer.id ?? "")}
														data-testid="confirm-reject-button">
														{t("confirm")}
													</Button>
													<Button
														variant="outlined"
														size="small"
														onClick={() => setRejectingId(null)}>
														{t("cancel")}
													</Button>
												</Box>
											</TableCell>
										</TableRow>
									)}
								</React.Fragment>
							))}
						</TableBody>
					</Table>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setOpen(false)}>{t("close")}</Button>
			</DialogActions>
		</Dialog>
	);
});

DialogProjectOffers.displayName = "DialogProjectOffers";
